import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import CashierMode from "./pages/CashierMode";
import VoucherManagement from "./pages/VoucherManagement";
import CreateVoucher from "./pages/CreateVoucher";
import UserManagement from "./pages/UserManagement";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import "./App.css";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    const nextPath = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(nextPath)}`} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === "kasir" ? "/kasir" : "/admin"} replace />;
  }

  return children;
};

// Redirect based on role
const RoleRedirect = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    const nextPath = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(nextPath)}`} replace />;
  }

  const quickScanCode = new URLSearchParams(location.search).get("s");
  if (quickScanCode) {
    return <Navigate to={`/kasir?scan=${encodeURIComponent(quickScanCode)}`} replace />;
  }

  return <Navigate to={user.role === "kasir" ? "/kasir" : "/admin"} replace />;
};


const ADMIN_ROLES = ["superadmin", "admin"];
const CASHIER_ROLES = ["superadmin", "admin", "kasir"];
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<RoleRedirect />} />
          
          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vouchers"
            element={
              <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                <VoucherManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vouchers/create"
            element={
              <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                <CreateVoucher />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                <Reports />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={ADMIN_ROLES}>
                <Settings />
              </ProtectedRoute>
            }
          />
          
          {/* Cashier Route */}
          <Route
            path="/kasir"
            element={
              <ProtectedRoute allowedRoles={CASHIER_ROLES}>
                <CashierMode />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}

export default App;
